'use client';

import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useOverlay } from '@/components/hooks/useOverlay';
import Overlay from '@/components/ui/Overlay';
import LayoutOverlay from '@/components/ui/LayoutOverlay';
import Block from '@/components/ui/Block';
import ListMissions from '@/components/ui/ListMissions';
import Hr from '@/components/ui/Hr';
import Well from '@/components/ui/Well';
import LayoutForm from '@/components/ui/LayoutForm';
import FormRow from '@/components/ui/FormRow';
import ControlInput from '@/components/ui/ControlInput';
import Buttons from '@/components/ui/Buttons';
import Button from '@/components/ui/Button';
import { JackpotPurchaseTicketItem } from '@/components/jackpot/jackpot-purchase-tickets/jackpot-purchase-ticket-item';
import { FrontCurrencyService, FrontUserBalanceService, GetFrontUserBalanceBalanceResponse } from '@/lib/jp-api/requests';
import { resolveActiveOfferFromTicketCount } from '@/lib/offers/resolve-active-offer-from-ticket-count';
import { formatCurrencyWithLabel } from '@/lib/currency-formatter';
import { getDefaultCurrency } from '@/lib/currency/currency-persistence';
import { CurrencySelector } from '@/components/currency-selector/currency-selector';
import { isAuthenticated } from '@/lib/helper/is-authenticated';
import { CurrencyContext } from '@/components/currency-provider/currency-provider';
import { useTranslations } from '@/lib/hooks/useTranslations';
import { getTicketInputNextValue } from '@/lib/offers/getTicketInputNextValue';
import { calculateChanceToWin } from '@/lib/offers/calculate-chance-to-win';
import { percentageFormatter } from '@/lib/formatter/percentageFormatter';
import { OverlayPurchaseTicketsConfirmMeta, OverlayPurchaseTicketsMeta } from '@/components/overlays/overlays';
import { integerFormatter } from '@/lib/formatter/integer-formatter';

export type ExchangeRateMap = Record<string, number>;

export function convertCurrency(rates: ExchangeRateMap, target: string, amount: number): number {
  return amount * rates[target];
}

const OverlayPurchaseTicketsClient = () => {
  const { isOverlayOpen, closeOverlay, overlayMeta } = useOverlay<OverlayPurchaseTicketsMeta>(`purchase-tickets`);
  const { ns, cmsParams, offers, baseOffer, ticketsInPool, ticket } = overlayMeta;

  const t = useTranslations(`OverlayPurchaseTickets`, ns);
  const isUserAuthenticated = isAuthenticated(cmsParams);

  const basePrice = Number(baseOffer.price) / baseOffer.minimumAmount;
  const { openOverlay: openConfirm } = useOverlay<OverlayPurchaseTicketsConfirmMeta>(`purchase-confirm`);
  const [activeOffer, setActiveOffer] = React.useState(overlayMeta.offer);
  const [ticketAmount, setTicketAmount] = useState<number>(overlayMeta.numberOfTickets);
  const [ticketPrice, setTicketPrice] = useState(basePrice);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRateMap>({} as ExchangeRateMap);
  const [userBalance, setUserBalance] = useState<GetFrontUserBalanceBalanceResponse>([]);

  const { currency } = useContext(CurrencyContext);
  const savings = ticketAmount * basePrice - ticketAmount * ticketPrice;

  useEffect(() => {
    if (!isUserAuthenticated) return;
    if (!isOverlayOpen) return;
    FrontUserBalanceService.getFrontUserBalanceBalance().then((data) => {
      setUserBalance(data);
    });
  }, [cmsParams, isUserAuthenticated, isOverlayOpen]);

  useEffect(() => {
    if (userBalance.length === 0) return;
    FrontCurrencyService.getFrontCurrencyRate({
      language: cmsParams.language,
      hostname: cmsParams.brand,
      source: getDefaultCurrency(),
      target: userBalance.map((c) => c.currencyCode),
    }).then((data) => {
      setExchangeRates(
        data.reduce<ExchangeRateMap>((acc, v) => {
          acc[v.target] = Number(v.rate);
          return acc;
        }, {}),
      );
    });
  }, [userBalance]);

  useEffect(() => {
    setActiveOffer(overlayMeta.offer);
  }, [overlayMeta.offer]);

  useEffect(() => {
    const { offer, price } = resolveActiveOfferFromTicketCount(baseOffer, offers, ticketAmount);
    setActiveOffer(offer);
    setTicketPrice(price);
  }, [ticketAmount, offers, baseOffer]);

  const totalPurchasePrice = convertCurrency(exchangeRates, currency, ticketAmount * ticketPrice);

  const isPurchaseButtonDisabled = ticketAmount <= 0;

  if (!isOverlayOpen) return null;
  return (
    <Overlay
      isOpen={isOverlayOpen}
      onClose={closeOverlay}
      layout="default"
      hideClose={true}
    >
      <LayoutOverlay
        size="wide"
        header={{
          title: offers.length ? t('overlaySuggestOffersTitle') : t('overlayOffersTitle'),
          onClose: closeOverlay,
        }}
      >
        <Block>
          {offers.length !== 0 && (
            <React.Fragment>
              <ListMissions layout="mobile-compact">
                {offers.map((offer) => (
                  <JackpotPurchaseTicketItem
                    ticket={ticket}
                    selected={activeOffer.id === offer.id}
                    key={offer.id}
                    offer={offer}
                    baseOffer={baseOffer}
                    onClick={() => {
                      setTicketAmount(offer.minimumAmount);
                    }}
                    onPurchaseButtonClick={() => {
                      setTicketAmount(offer.minimumAmount);
                    }}
                    buttonDescription={
                      offer.footerText
                        ? offer.footerText
                        : offer.stats.soldTickets
                          ? t('{{countFormatted}} ticketsPurchased', {
                              count: offer.stats.soldTickets,
                              countFormatted: integerFormatter.format(offer.stats.soldTickets),
                            })
                          : ''
                    }
                  />
                ))}
              </ListMissions>

              <Hr layout="striped" />
            </React.Fragment>
          )}

          <Well
            theme="checkout"
            className="checkout"
          >
            <h3>Your Order Summary</h3>
            <div className="checkout__columns">
              <div className="checkout__columns__column">
                <LayoutForm>
                  <FormRow label="Ticket Amount">
                    <ControlInput
                      theme="dark"
                      type="text"
                      value={ticketAmount.toString()}
                      onChange={(e) => {
                        const nextValue = getTicketInputNextValue(e.target.value);
                        if (nextValue === null) return;
                        setTicketAmount(nextValue);
                      }}
                      name="sum"
                      placeholder={t('Amount of tickets')}
                      addOn={formatCurrencyWithLabel(getDefaultCurrency(), ticketAmount * ticketPrice)}
                    />
                  </FormRow>
                </LayoutForm>
                <p className="font-size-smaller text-intent-primary">
                  {t('chanceToWin {{chanceToWin}}', {
                    chanceToWin: percentageFormatter.format(calculateChanceToWin(ticketAmount, ticketsInPool)),
                  })}
                </p>
              </div>
              <div className="checkout__columns__column align-right text-intent-primary">
                {savings !== 0 && (
                  <p className="checkout__savings">
                    You save {formatCurrencyWithLabel(currency, convertCurrency(exchangeRates, currency, savings))}
                  </p>
                )}
                <p className="font-size-large nowrap bold">Total: {formatCurrencyWithLabel(currency, totalPurchasePrice)}</p>
                <p className="font-size-smaller nowrap">
                  which equals{' '}
                  <span className="semibold text-intent-highlight">{formatCurrencyWithLabel('USD', ticketAmount * ticketPrice)}</span>
                </p>
              </div>
            </div>
          </Well>
          <div className="checkout checkout__columns">
            <div className="checkout__columns__column">
              <ul className="tickets-tools">
                <li>
                  <div className="tickets-tools__label">{t('balance')}</div>
                  <CurrencySelector
                    cmsParams={cmsParams}
                    ns={ns}
                  />
                </li>
              </ul>
            </div>
            <div className="checkout__columns__column">
              <Buttons>
                <Button
                  title={t('btnCancel')}
                  onClick={closeOverlay}
                />
                <Button
                  title={t('btnBuyTickets')}
                  theme="primary"
                  disabled={isPurchaseButtonDisabled}
                  onClick={(e) => {
                    openConfirm(e, {
                      asNew: true,
                      closeRestOverlays: false,
                      meta: {
                        ns,
                        cmsParams,
                        ticketAmount: ticketAmount,
                        ticket,
                        baseOffer,
                        offer: activeOffer,
                        exchangeRates,
                        total: formatCurrencyWithLabel(currency, totalPurchasePrice),
                      },
                    });
                  }}
                />
              </Buttons>
            </div>
          </div>
        </Block>
      </LayoutOverlay>
    </Overlay>
  );
};

OverlayPurchaseTicketsClient.displayName = 'OverlayPurchaseTicketsClient';

export default OverlayPurchaseTicketsClient;
